import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema, MoneySchema } from '../common/index.js';
import { VenuParSchema } from './premier-contact.js';

/** Dossiers, procédure P0 à P9, tâches, décisions, échéances, journal (architecture §2.2, §2.4, §2.9). */

/** Squelette commun (ecrans/arbre-de-decision.md) ; P1, P5 et P6 sont déduits de l'arbre, libellés à valider. */
export const PHASES = [
  { code: 'P0', libelle: 'Premier contact' },
  { code: 'P1', libelle: 'Valeur du bien et faisabilité' },
  { code: 'P2', libelle: 'Devis, acompte, pouvoirs' },
  { code: 'P3', libelle: 'Généalogie : vivants et défunts' },
  { code: 'P4', libelle: 'Clerc : interrogations notariales' },
  { code: 'P5', libelle: 'Titres de propriété' },
  { code: 'P6', libelle: 'Déclaration de succession' },
  { code: 'P7', libelle: 'Acte de notoriété et attestation immobilière' },
  { code: 'P8', libelle: 'Second devis : partager, vendre, SCI, céder' },
  { code: 'P9', libelle: 'Chez le notaire : chacun titré' },
] as const;

export const PhaseSchema = z
  .enum(['P0', 'P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9'])
  .meta({ id: 'Phase', description: 'Phase du squelette commun P0 à P9.' });

export const DossierStatusSchema = z.enum(['prospect', 'actif', 'bloque', 'clos', 'abandonne']).meta({ id: 'DossierStatus' });

export const BlockerSchema = z
  .object({ kind: z.string().min(1), label: z.string().min(1), since: LocalDateSchema })
  .meta({ id: 'Blocker', description: 'Blocage courant du dossier.' });

export const ValueRangeSchema = z.object({ low: MoneySchema, high: MoneySchema }).meta({ id: 'ValueRange' });

/** Statut métier du dossier (STA-01 à STA-09), libellé servi par le référentiel versionné (RFC-L51-001 §1). */
export const StatutMetierSchema = z
  .strictObject({ code: z.string().regex(/^STA-\d{2}$/), libelle: z.string().min(1) })
  .meta({ id: 'StatutMetier', description: 'Statut métier du dossier (référentiel STA-01 à STA-09).' });

export const DossierSummarySchema = z
  .object({
    id: IdSchema,
    reference: z.string().regex(/^[A-Z]{3,4}-\d{4}-\d{3}$/),
    title: z.string().min(1),
    status: DossierStatusSchema,
    currentPhase: PhaseSchema,
    blocker: BlockerSchema.nullable(),
    estimatedValue: ValueRangeSchema.nullable(),
    etudeName: z.string().nullable(),
    heirsCount: z.number().int().min(0),
    respondedCount: z.number().int().min(0),
    toFollowUpCount: z.number().int().min(0),
    unreachableCount: z.number().int().min(0),
    /** Dernier statut métier consigné ; null si aucun (RFC-L51-001 §1). */
    statutMetier: StatutMetierSchema.nullable().optional(),
  })
  .meta({ id: 'DossierSummary', description: 'Ligne de liste d’un dossier (équipe).' });

export const StepSchema = z
  .object({
    id: IdSchema,
    phase: PhaseSchema,
    label: z.string().min(1),
    orderIndex: z.number().int().min(0),
    status: z.enum(['a_faire', 'en_cours', 'fait', 'saute']),
    skippedReason: z.string().nullable(),
    dueAt: LocalDateSchema.nullable(),
  })
  .meta({ id: 'Step', description: 'Étape de procédure du dossier (modifiable par dossier).' });

export const StepCreateSchema = z
  .object({ phase: PhaseSchema, label: z.string().min(1), afterStepId: IdSchema.nullable() })
  .meta({ id: 'StepCreate' });
export const StepOrderSchema = z.object({ stepIds: z.array(IdSchema).min(1) }).meta({ id: 'StepOrder' });
export const StepSkipSchema = z.object({ reason: z.string().min(3) }).meta({ id: 'StepSkip' });

export const DeadlineSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    successionId: IdSchema.nullable(),
    kind: z.string().min(1),
    label: z.string().min(1),
    dueAt: LocalDateSchema,
    status: z.enum(['a_venir', 'proche', 'depassee', 'faite']),
    parameterKeys: z.array(z.string()),
  })
  .meta({ id: 'Deadline', description: 'Échéance légale calculée depuis des paramètres datés.' });

export const MandateStatusSchema = z.enum(['absent', 'brouillon', 'envoye', 'signe', 'refuse', 'revoque']).meta({ id: 'MandateStatus' });

export const DossierStaffViewSchema = DossierSummarySchema.extend({
  mandantPersonId: IdSchema.nullable(),
  mandateStatus: MandateStatusSchema,
  nextAction: z.string().nullable(),
  completeness: z.number().int().min(0).max(100),
  steps: z.array(StepSchema),
  deadlines: z.array(DeadlineSchema),
  closedAt: InstantSchema.nullable(),
  externalAccessUntil: InstantSchema.nullable(),
  rowVersion: z.number().int().min(0),
  /** Type de mission (TYP-01 à TYP-06) ; null pour un dossier ancien sans trace. */
  typeCode: z.string().regex(/^TYP-\d{2}$/).nullable().optional(),
  /** Réponse à « Qui est venu vous voir ? » ; null sans réponse. Vue d'équipe : hors équipe MAAT, dossiers.lire est refusé. */
  venuPar: VenuParSchema.nullable().optional(),
}).meta({ id: 'DossierStaffView', description: 'Vue d’ensemble d’un dossier (équipe MAAT).' });

export const DossierCreateSchema = z
  .object({
    title: z.string().min(1),
    mandantPersonId: IdSchema.nullable(),
    etudeId: IdSchema.nullable(),
    /** Type de mission (TYP-01 à TYP-06) ; absent : le premier type du référentiel. */
    typeCode: z.string().regex(/^TYP-\d{2}$/).optional(),
    /** Réponse à « Qui est venu vous voir ? » (premier contact) ; facultative. */
    venuPar: VenuParSchema.optional(),
  })
  .meta({ id: 'DossierCreate' });

export const DossierPatchSchema = z
  .object({
    title: z.string().min(1).optional(),
    status: DossierStatusSchema.optional(),
    blocker: BlockerSchema.nullable().optional(),
  })
  .meta({ id: 'DossierPatch' });

export const DashboardSchema = z
  .object({
    dossiers: z.array(DossierSummarySchema),
    pendingDecisions: z.number().int().min(0),
    collected: MoneySchema,
    outstanding: MoneySchema,
    teamActivity: z.array(z.object({ userDisplayName: z.string(), actionsToday: z.number().int().min(0) })),
    /** Les statuts du référentiel, dans son ordre, avec le nombre de dossiers ouverts qui les portent, zéro compris (RFC-L51-001 §1). */
    statuts: z.array(StatutMetierSchema.extend({ nombre: z.number().int().min(0) })).optional(),
  })
  .meta({ id: 'Dashboard', description: 'Tableau de bord de la direction (pilotage financier).' });

export const TaskKindSchema = z
  .enum(['appel', 'valider_relance', 'verifier_piece', 'notaire', 'greffe', 'decision'])
  .meta({ id: 'TaskKind' });

export const TaskSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema.nullable(),
    personId: IdSchema.nullable(),
    kind: TaskKindSchema,
    title: z.string().min(1),
    assigneeUserId: IdSchema,
    dueAt: InstantSchema,
    status: z.enum(['a_faire', 'fait', 'reportee']),
    rowVersion: z.number().int().min(0),
  })
  .meta({ id: 'Task', description: 'Tâche de la file du jour.' });

export const TaskCreateSchema = z
  .object({
    dossierId: IdSchema.nullable(),
    personId: IdSchema.nullable(),
    kind: TaskKindSchema,
    title: z.string().min(1),
    assigneeUserId: IdSchema,
    dueAt: InstantSchema,
  })
  .meta({ id: 'TaskCreate' });

export const TaskPatchSchema = z
  .object({ status: z.enum(['a_faire', 'fait', 'reportee']).optional(), dueAt: InstantSchema.optional() })
  .meta({ id: 'TaskPatch' });

export const DecisionSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    question: z.string().min(1),
    options: z.array(z.object({ code: z.string().min(1), label: z.string().min(1), costEstimate: MoneySchema.nullable() })).min(2),
    reservedToRole: z.enum(['dirigeante', 'admin']),
    decidedOption: z.string().nullable(),
    decidedAt: InstantSchema.nullable(),
  })
  .meta({ id: 'Decision', description: 'Décision réservée à Isabelle.' });

export const DecisionAnswerSchema = z.object({ option: z.string().min(1), comment: z.string().optional() }).meta({ id: 'DecisionAnswer' });

export const TimelineEntrySchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    personId: IdSchema.nullable(),
    kind: z.enum(['message', 'appel', 'piece', 'relance', 'decision', 'etape', 'consentement']),
    at: InstantSchema,
    summary: z.string().min(1),
    visibility: z.enum(['equipe', 'externe']),
  })
  .meta({ id: 'TimelineEntry', description: 'Entrée du journal horodaté (immuable).' });

export const ExportSchema = z
  .object({ documentId: IdSchema, downloadUrl: z.string().min(1), format: z.enum(['pdf', 'gedcom']), expiresAt: InstantSchema })
  .meta({ id: 'Export', description: 'Export généré (sans coulisses pour un destinataire externe).' });
